import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { reservationsAPI, exportsAPI } from '../services/api';
import Pagination from './Pagination';
import ExportCsvButton from './ExportCsvButton';
import './Users.css';

const STATUS_LABELS = {
  reserved_pending_deposit: 'Awaiting deposit',
  reserved_paid_deposit: 'Deposit paid',
  completed: 'Completed',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

const initialPerPage = () => {
  const stored = parseInt(localStorage.getItem('per_page') || '', 10);
  return [10, 15, 25, 50, 100].includes(stored) ? stored : 15;
};

const Reservations = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: initialPerPage(),
    total: 0,
  });
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const prevDebouncedSearch = useRef('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (prevDebouncedSearch.current === debouncedSearch) return;
    prevDebouncedSearch.current = debouncedSearch;
    setPagination((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch]);

  const fetchReservations = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
      };
      if (statusFilter) params.status = statusFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await reservationsAPI.getAll(params);
      setRows(response.data.data || []);
      const pg = response.data.pagination;
      if (pg) {
        setPagination((prev) => ({
          ...prev,
          page: pg.page ?? prev.page,
          per_page: pg.per_page ?? prev.per_page,
          total: pg.total ?? 0,
        }));
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || error.message || 'Failed to load reservations',
      );
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page, statusFilter, debouncedSearch, dateFrom, dateTo]);

  useEffect(() => {
    void fetchReservations();
  }, [fetchReservations]);

  const handleClearFilters = () => {
    setStatusFilter('');
    setSearchInput('');
    setDebouncedSearch('');
    prevDebouncedSearch.current = '';
    setDateFrom('');
    setDateTo('');
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const buyerName = (b) => {
    if (!b) return '—';
    const name = [b.first_name, b.last_name].filter(Boolean).join(' ');
    return name || b.email || '—';
  };

  if (loading && rows.length === 0) {
    return <div className="loading">Loading reservations…</div>;
  }

  return (
    <div className="users-container">
      <p style={{ marginBottom: '16px', color: 'var(--text-secondary)', fontSize: '14px' }}>
        Deposit reservations hold inventory until the buyer pays a deposit online. After the balance is
        collected, convert eligible reservations into confirmed orders.
      </p>

      <div
        className="search-filter-bar"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          alignItems: 'flex-end',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label htmlFor="res-status" style={{ fontSize: '12px', fontWeight: 600 }}>
            Status
          </label>
          <select
            id="res-status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPagination((p) => ({ ...p, page: 1 }));
            }}
            style={{ minWidth: '180px', padding: '8px' }}
          >
            <option value="">All</option>
            <option value="reserved_pending_deposit">Awaiting deposit</option>
            <option value="reserved_paid_deposit">Deposit paid</option>
            <option value="completed">Completed</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1 1 200px' }}>
          <label htmlFor="res-search" style={{ fontSize: '12px', fontWeight: 600 }}>
            Search
          </label>
          <input
            id="res-search"
            type="search"
            placeholder="Product, buyer, order #…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ padding: '8px' }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label htmlFor="res-from" style={{ fontSize: '12px', fontWeight: 600 }}>
            From
          </label>
          <input
            id="res-from"
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPagination((p) => ({ ...p, page: 1 }));
            }}
            style={{ padding: '8px' }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label htmlFor="res-to" style={{ fontSize: '12px', fontWeight: 600 }}>
            To
          </label>
          <input
            id="res-to"
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPagination((p) => ({ ...p, page: 1 }));
            }}
            style={{ padding: '8px' }}
          />
        </div>

        <button type="button" className="btn btn-secondary" onClick={handleClearFilters}>
          Clear filters
        </button>
        <ExportCsvButton
          exportRequest={exportsAPI.reservations}
          filters={{
            status: statusFilter || undefined,
            search: debouncedSearch || undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
          }}
          filename="reservations.csv"
        />
      </div>

      {loading && rows.length > 0 ? (
        <div className="loading" style={{ padding: '8px' }}>
          Updating…
        </div>
      ) : null}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Buyer</th>
              <th>Deposit</th>
              <th>Balance</th>
              <th>Status</th>
              <th>Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                  No reservations found
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{row.product?.title || '—'}</div>
                    {row.product?.brand ? (
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {row.product.brand}
                      </div>
                    ) : null}
                  </td>
                  <td>{buyerName(row.buyer)}</td>
                  <td>
                    {row.currency} {row.deposit_amount}
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                      {row.deposit_percent}%
                    </div>
                  </td>
                  <td>
                    {row.currency} {row.balance_amount}
                  </td>
                  <td>
                    <span
                      className={`status-badge status-${row.status}`}
                      style={{ textTransform: 'none', fontSize: '12px' }}
                    >
                      {STATUS_LABELS[row.status] || row.status}
                    </span>
                  </td>
                  <td>{row.order?.order_number || '—'}</td>
                  <td className="actions" style={{ whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => navigate(`/reservations/${row.id}`)}
                    >
                      Details
                    </button>
                    {row.can_delete !== false ? (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        style={{ marginLeft: '6px' }}
                        onClick={async () => {
                          if (
                            !window.confirm(
                              'Delete this reservation? Active holds will be released.',
                            )
                          ) {
                            return;
                          }
                          try {
                            await reservationsAPI.delete(row.id);
                            toast.success('Reservation deleted.');
                            void fetchReservations();
                          } catch (error) {
                            toast.error(
                              error.response?.data?.message ||
                                error.message ||
                                'Could not delete reservation',
                            );
                          }
                        }}
                      >
                        Delete
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.total > pagination.per_page ? (
        <Pagination
          pagination={pagination}
          onPageChange={(page) => setPagination((p) => ({ ...p, page }))}
          onPerPageChange={(per_page) => setPagination((p) => ({ ...p, per_page, page: 1 }))}
        />
      ) : null}
    </div>
  );
};

export default Reservations;
